import VillageModal from "@/components/modals/VillageModal";
import { convertLetters } from "@/utils/letterConversion";
import { gql, useMutation, useQuery } from "@apollo/client";
import {
    CheckCircle,
    Clock,
    Copy,
    Download,
    Edit2,
    Link as LinkIcon,
    Loader2,
    MessageSquare,
    Settings,
    Trash2,
    Twitch as TwitchIcon,
    Unlink,
    User,
} from "lucide-react";
import { useEffect, useState } from "react";

const MY_ACCOUNT_QUERY = gql`
    query MyAccount {
        myAccount {
            hasDiscord
            isStreamTeam
            isStreamTeamAcademy
            canRequestStreamTeam
            hasRunestone
            runestones {
                id
                imageUrl
                runeName
                createdAt
                __typename
            }
            discordAuth {
                id
                discordId
                discordUsername
                discordDiscriminator
                discordAvatar
                __typename
            }
            streamTeamMember {
                id
                bio
                twitchUsername
                isAcademy
                isActive
                joinedAt
                hasTwitchAuth
                hasCentralBotAuth
                canCreateEvents
                twitterUrl
                youtubeUrl
                discordUrl
                instagramUrl
                tiktokUrl
                twitchAuth {
                    id
                    twitchUsername
                    scopes
                    streamTeamRewardId
                    streamTeamRewardEnabled
                    __typename
                }
                __typename
            }
            pendingStreamRequest {
                id
                message
                status
                createdAt
                isAcademyApplication
                __typename
            }
            pendingStreamInvite {
                id
                message
                invitedBy {
                    id
                    name
                    __typename
                }
                createdAt
                __typename
            }
            __typename
        }
        streamSettings {
            id
            acceptingApplications
            acceptingAcademyApplications
        }
        featureFlags {
            streamTeamChannelPointRewardEnabled
            academyRunestoneSystemEnabled
        }
    }
`;

const REQUEST_STREAM_TEAM_MUTATION = gql`
    mutation RequestStreamTeam(
        $message: String!
        $isAcademyApplication: Boolean
    ) {
        requestStreamTeam(
            message: $message
            isAcademyApplication: $isAcademyApplication
        ) {
            success
            request {
                id
                status
            }
        }
    }
`;

const RESPOND_TO_INVITE_MUTATION = gql`
    mutation RespondToStreamTeamInvite($inviteId: UUID!, $accept: Boolean!) {
        respondToStreamTeamInvite(inviteId: $inviteId, accept: $accept) {
            success
            streamTeamMember {
                id
            }
        }
    }
`;

const GENERATE_LINKING_TOKEN = gql`
    mutation GenerateLinkingToken {
        generateLinkingToken {
            token
            username
            twitchId
        }
    }
`;

const UPDATE_STREAM_TEAM_MEMBER_MUTATION = gql`
    mutation UpdateStreamTeamMember(
        $bio: String
        $isActive: Boolean
        $twitterUrl: String
        $youtubeUrl: String
        $discordUrl: String
        $instagramUrl: String
        $tiktokUrl: String
    ) {
        updateStreamTeamMember(
            bio: $bio
            isActive: $isActive
            twitterUrl: $twitterUrl
            youtubeUrl: $youtubeUrl
            discordUrl: $discordUrl
            instagramUrl: $instagramUrl
            tiktokUrl: $tiktokUrl
        ) {
            success
            streamTeamMember {
                id
                bio
                isActive
                twitterUrl
                youtubeUrl
                discordUrl
                instagramUrl
                tiktokUrl
            }
        }
    }
`;

const UNLINK_DISCORD_MUTATION = gql`
    mutation UnlinkDiscord {
        unlinkDiscord {
            success
        }
    }
`;

const SYNC_MY_STREAM_TEAM_CHANNEL_POINT_REWARD_MUTATION = gql`
    mutation SyncMyStreamTeamChannelPointReward {
        syncMyStreamTeamChannelPointReward {
            success
            rewardId
        }
    }
`;

const REMOVE_MY_STREAM_TEAM_CHANNEL_POINT_REWARD_MUTATION = gql`
    mutation RemoveMyStreamTeamChannelPointReward {
        removeMyStreamTeamChannelPointReward {
            success
        }
    }
`;

const MY_STREAM_TEAM_EVENTS_QUERY = gql`
    query MyStreamTeamEvents {
        myStreamTeamEvents {
            id
            title
            description
            summary
            eventType
            status
            locationType
            locationDetails
            startTime
            endTime
            joinUrl
            joinInstructions
            createdAt
        }
    }
`;

const CREATE_STREAM_TEAM_EVENT_MUTATION = gql`
    mutation CreateStreamTeamEvent(
        $title: String!
        $description: String
        $summary: String
        $eventType: String!
        $status: String
        $locationType: String
        $locationDetails: String
        $startTime: DateTime!
        $endTime: DateTime
        $joinUrl: String
        $joinInstructions: String
    ) {
        createStreamTeamEvent(
            title: $title
            description: $description
            summary: $summary
            eventType: $eventType
            status: $status
            locationType: $locationType
            locationDetails: $locationDetails
            startTime: $startTime
            endTime: $endTime
            joinUrl: $joinUrl
            joinInstructions: $joinInstructions
        ) {
            success
            event {
                id
                title
            }
        }
    }
`;

const UPDATE_STREAM_TEAM_EVENT_MUTATION = gql`
    mutation UpdateStreamTeamEvent(
        $eventId: UUID!
        $title: String
        $description: String
        $summary: String
        $eventType: String
        $status: String
        $locationType: String
        $locationDetails: String
        $startTime: DateTime
        $endTime: DateTime
        $joinUrl: String
        $joinInstructions: String
    ) {
        updateStreamTeamEvent(
            eventId: $eventId
            title: $title
            description: $description
            summary: $summary
            eventType: $eventType
            status: $status
            locationType: $locationType
            locationDetails: $locationDetails
            startTime: $startTime
            endTime: $endTime
            joinUrl: $joinUrl
            joinInstructions: $joinInstructions
        ) {
            success
            event {
                id
                title
            }
        }
    }
`;

const DELETE_STREAM_TEAM_EVENT_MUTATION = gql`
    mutation DeleteStreamTeamEvent($eventId: UUID!) {
        deleteStreamTeamEvent(eventId: $eventId) {
            success
        }
    }
`;

const STREAM_TEAM_BIO_MAX_LENGTH = 500;

export default function MyAccountPage() {
    const { data, loading, refetch } = useQuery(MY_ACCOUNT_QUERY);
    const {
        data: eventsData,
        loading: eventsLoading,
        refetch: refetchEvents,
    } = useQuery(MY_STREAM_TEAM_EVENTS_QUERY, {
        skip: !data?.myAccount?.streamTeamMember?.canCreateEvents,
    });
    const [requestStreamTeam] = useMutation(REQUEST_STREAM_TEAM_MUTATION);
    const [respondToInvite] = useMutation(RESPOND_TO_INVITE_MUTATION);
    const [updateStreamTeamMember] = useMutation(
        UPDATE_STREAM_TEAM_MEMBER_MUTATION,
    );
    const [unlinkDiscord] = useMutation(UNLINK_DISCORD_MUTATION);
    const [syncMyStreamTeamChannelPointReward] = useMutation(
        SYNC_MY_STREAM_TEAM_CHANNEL_POINT_REWARD_MUTATION,
    );
    const [removeMyStreamTeamChannelPointReward] = useMutation(
        REMOVE_MY_STREAM_TEAM_CHANNEL_POINT_REWARD_MUTATION,
    );
    const [createStreamTeamEvent] = useMutation(
        CREATE_STREAM_TEAM_EVENT_MUTATION,
    );
    const [updateStreamTeamEvent] = useMutation(
        UPDATE_STREAM_TEAM_EVENT_MUTATION,
    );
    const [deleteStreamTeamEvent] = useMutation(
        DELETE_STREAM_TEAM_EVENT_MUTATION,
    );

    const [requestMessage, setRequestMessage] = useState("");
    const [bio, setBio] = useState("");
    const [requestForm, setRequestForm] = useState<
        "ravenguard" | "academy" | null
    >(null);
    const [showBioEditor, setShowBioEditor] = useState(false);
    const [showSocialLinksEditor, setShowSocialLinksEditor] = useState(false);
    const [showEventForm, setShowEventForm] = useState(false);
    const [editingEvent, setEditingEvent] = useState<any>(null);
    const [twitterUrl, setTwitterUrl] = useState("");
    const [youtubeUrl, setYoutubeUrl] = useState("");
    const [discordUrl, setDiscordUrl] = useState("");
    const [instagramUrl, setInstagramUrl] = useState("");
    const [tiktokUrl, setTiktokUrl] = useState("");

    // Event form state
    const [eventTitle, setEventTitle] = useState("");
    const [eventDescription, setEventDescription] = useState("");
    const [eventSummary, setEventSummary] = useState("");
    const [eventType, setEventType] = useState("community");
    const [eventStatus, setEventStatus] = useState("draft");
    const [eventLocationType, setEventLocationType] = useState("online");
    const [eventLocationDetails, setEventLocationDetails] = useState("");
    const [eventStartTime, setEventStartTime] = useState("");
    const [eventEndTime, setEventEndTime] = useState("");
    const [eventJoinUrl, setEventJoinUrl] = useState("");
    const [eventJoinInstructions, setEventJoinInstructions] = useState("");

    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [modalConfig, setModalConfig] = useState<{
        title: string;
        message: string;
        type: "alert" | "confirm" | "error" | "success" | "info";
        onConfirm?: () => void;
    }>({
        title: "",
        message: "",
        type: "info",
    });

    const showModal = (
        title: string,
        message: string,
        type: "alert" | "confirm" | "error" | "success" | "info" = "info",
        onConfirm?: () => void,
    ) => {
        setModalConfig({ title, message, type, onConfirm });
        setModalOpen(true);
    };

    // Check for OAuth success/error in URL params and refetch
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get("discord_success") === "true") {
            console.debug(
                "[Discord OAuth] Success callback detected, refetching data...",
            );
            refetch();
            // Clean up URL
            window.history.replaceState({}, "", window.location.pathname);
        } else if (params.get("discord_error")) {
            console.error(
                "[Discord OAuth] Error:",
                params.get("discord_error"),
            );
            showModal(
                "Discord Error",
                `Discord linking failed: ${params.get("discord_error")}`,
                "error",
            );
            window.history.replaceState({}, "", window.location.pathname);
        }

        if (params.get("twitch_success") === "true") {
            console.debug(
                "[Twitch OAuth] Success callback detected, refetching data...",
            );
            refetch();
            window.history.replaceState({}, "", window.location.pathname);
        } else if (params.get("twitch_error")) {
            console.error("[Twitch OAuth] Error:", params.get("twitch_error"));
            showModal(
                "Twitch Error",
                `Twitch linking failed: ${params.get("twitch_error")}`,
                "error",
            );
            window.history.replaceState({}, "", window.location.pathname);
        }

        if (params.get("central_bot_success") === "true") {
            console.debug(
                "[RVNSBOT OAuth] Success callback detected, refetching data...",
            );
            refetch();
            window.history.replaceState({}, "", window.location.pathname);
        } else if (params.get("central_bot_error")) {
            console.error(
                "[RVNSBOT OAuth] Error:",
                params.get("central_bot_error"),
            );
            showModal(
                "RVNSBOT Error",
                `RVNSBOT authorization failed: ${params.get("central_bot_error")}`,
                "error",
            );
            window.history.replaceState({}, "", window.location.pathname);
        }
    }, [refetch]);

    if (loading) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center">
                <div className="text-center">
                    <div className="border-brand inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]" />
                    <p className="text-stats font-garamond mt-4 text-lg">
                        Loading account...
                    </p>
                </div>
            </div>
        );
    }

    const account = data?.myAccount;
    const isAcademyMember = Boolean(
        account?.isStreamTeamAcademy || account?.streamTeamMember?.isAcademy,
    );

    const handleRequestStreamTeam = async () => {
        if (!requestMessage.trim()) {
            showModal(
                "Validation Error",
                "Please provide a message explaining why you'd like to join the stream team.",
                "error",
            );
            return;
        }

        try {
            await requestStreamTeam({
                variables: {
                    message: requestMessage,
                    isAcademyApplication: requestForm === "academy",
                },
            });
            setRequestMessage("");
            setRequestForm(null);
            refetch();
        } catch (error) {
            console.error("Error requesting stream team:", error);
            showModal(
                "Error",
                "Failed to submit request. Please try again.",
                "error",
            );
        }
    };

    const handleRespondToInvite = async (accept: boolean) => {
        if (!account?.pendingStreamInvite) return;

        try {
            await respondToInvite({
                variables: {
                    inviteId: account.pendingStreamInvite.id,
                    accept,
                },
            });
            refetch();
        } catch (error) {
            console.error("Error responding to invite:", error);
            showModal(
                "Error",
                "Failed to respond to invite. Please try again.",
                "error",
            );
        }
    };

    const handleUpdateBio = async () => {
        if (showBioEditor && bio.length > STREAM_TEAM_BIO_MAX_LENGTH) {
            showModal(
                "Validation Error",
                `Bio must be ${STREAM_TEAM_BIO_MAX_LENGTH} characters or fewer.`,
                "error",
            );
            return;
        }

        try {
            const variables: any = {};

            // Only include bio if bio editor is open
            if (showBioEditor) {
                variables.bio = bio;
            }

            // Only include social links if social links editor is open
            if (showSocialLinksEditor) {
                variables.twitterUrl = twitterUrl || "";
                variables.youtubeUrl = youtubeUrl || "";
                variables.discordUrl = discordUrl || "";
                variables.instagramUrl = instagramUrl || "";
                variables.tiktokUrl = tiktokUrl || "";
            }

            await updateStreamTeamMember({ variables });
            setShowBioEditor(false);
            setShowSocialLinksEditor(false);
            refetch();
        } catch (error) {
            console.error("Error updating profile:", error);
            showModal(
                "Error",
                "Failed to update profile. Please try again.",
                "error",
            );
        }
    };

    const resetEventForm = () => {
        setEventTitle("");
        setEventDescription("");
        setEventSummary("");
        setEventType("community");
        setEventStatus("draft");
        setEventLocationType("online");
        setEventLocationDetails("");
        setEventStartTime("");
        setEventEndTime("");
        setEventJoinUrl("");
        setEventJoinInstructions("");
        setEditingEvent(null);
    };

    // Helper to convert UTC date to local datetime-local format
    const utcToLocalDatetimeString = (utcDateString: string): string => {
        const date = new Date(utcDateString);
        // Get local date components
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    // Get user's timezone name
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const openEditEventForm = (event: any) => {
        setEventTitle(event.title);
        setEventDescription(event.description || "");
        setEventSummary(event.summary || "");
        // Normalize to lowercase to match select options
        setEventType((event.eventType || "community").toLowerCase());
        setEventStatus((event.status || "draft").toLowerCase());
        setEventLocationType((event.locationType || "online").toLowerCase());
        setEventLocationDetails(event.locationDetails || "");
        // Convert UTC to local time for the datetime-local input
        setEventStartTime(utcToLocalDatetimeString(event.startTime));
        setEventEndTime(
            event.endTime ? utcToLocalDatetimeString(event.endTime) : "",
        );
        setEventJoinUrl(event.joinUrl || "");
        setEventJoinInstructions(event.joinInstructions || "");
        setEditingEvent(event);
        setShowEventForm(true);
    };

    const handleCreateOrUpdateEvent = async () => {
        if (!eventTitle.trim()) {
            showModal("Validation Error", "Event title is required.", "error");
            return;
        }
        if (!eventStartTime) {
            showModal("Validation Error", "Start time is required.", "error");
            return;
        }

        // datetime-local value is in local time, convert to ISO for API
        const startTimeISO = new Date(eventStartTime).toISOString();
        const endTimeISO = eventEndTime
            ? new Date(eventEndTime).toISOString()
            : null;

        try {
            if (editingEvent) {
                await updateStreamTeamEvent({
                    variables: {
                        eventId: editingEvent.id,
                        title: eventTitle,
                        description: eventDescription,
                        summary: eventSummary,
                        eventType,
                        status: eventStatus,
                        locationType: eventLocationType,
                        locationDetails: eventLocationDetails,
                        startTime: startTimeISO,
                        endTime: endTimeISO,
                        joinUrl: eventJoinUrl,
                        joinInstructions: eventJoinInstructions,
                    },
                });
                showModal("Success", "Event updated successfully!", "success");
            } else {
                await createStreamTeamEvent({
                    variables: {
                        title: eventTitle,
                        description: eventDescription,
                        summary: eventSummary,
                        eventType,
                        status: eventStatus,
                        locationType: eventLocationType,
                        locationDetails: eventLocationDetails,
                        startTime: startTimeISO,
                        endTime: endTimeISO,
                        joinUrl: eventJoinUrl,
                        joinInstructions: eventJoinInstructions,
                    },
                });
                showModal("Success", "Event created successfully!", "success");
            }
            setShowEventForm(false);
            resetEventForm();
            refetchEvents();
        } catch (error) {
            console.error("Error saving event:", error);
            showModal(
                "Error",
                "Failed to save event. Please try again.",
                "error",
            );
        }
    };

    const handleDeleteEvent = (eventId: string, eventTitle: string) => {
        showModal(
            "Confirm Delete",
            `Are you sure you want to delete "${eventTitle}"?`,
            "confirm",
            async () => {
                try {
                    await deleteStreamTeamEvent({
                        variables: { eventId },
                    });
                    refetchEvents();
                    showModal(
                        "Success",
                        "Event deleted successfully.",
                        "success",
                    );
                } catch (error) {
                    console.error("Error deleting event:", error);
                    showModal(
                        "Error",
                        "Failed to delete event. Please try again.",
                        "error",
                    );
                }
            },
        );
    };

    const handleUnlinkDiscord = async () => {
        showModal(
            "Confirm Unlink",
            "Are you sure you want to unlink your Discord account?",
            "confirm",
            async () => {
                try {
                    await unlinkDiscord();
                    refetch();
                } catch (error) {
                    console.error("Error unlinking Discord:", error);
                    showModal(
                        "Error",
                        "Failed to unlink Discord. Please try again.",
                        "error",
                    );
                }
            },
        );
    };

    // The Apollo client uses errorPolicy "all", so GraphQL errors resolve rather than throw.
    const showRewardError = (graphQLError: any, fallback: string) => {
        const code = graphQLError?.extensions?.code || "UNKNOWN";
        const titles: Record<string, string> = {
            REAUTH_REQUIRED: "Reconnect Twitch",
            TWITCH_NOT_AFFILIATE: "Affiliate Required",
            TWITCH_REWARD_DUPLICATE: "Reward Already Exists",
            TWITCH_TOO_MANY_REWARDS: "Too Many Rewards",
            TWITCH_REWARD_FORBIDDEN: "Twitch Refused the Request",
            TWITCH_RATE_LIMITED: "Twitch Is Busy",
            TWITCH_UNAVAILABLE: "Twitch Unavailable",
            TWITCH_REWARD_INVALID_CONFIG: "Reward Configuration Problem",
            FEATURE_DISABLED: "Rewards Disabled",
        };

        showModal(
            titles[code] || "Error",
            graphQLError?.message || fallback,
            "error",
        );
    };

    const handleSyncMyReward = async () => {
        try {
            const result = await syncMyStreamTeamChannelPointReward();
            const graphQLError = result?.errors?.[0];
            if (graphQLError) {
                showRewardError(
                    graphQLError,
                    "Failed to sync your reward. Please try again.",
                );
                return;
            }

            refetch();
            showModal(
                "Success",
                "Your stream team channel point reward is now synced.",
                "success",
            );
        } catch (error: any) {
            showRewardError(
                error?.graphQLErrors?.[0],
                "Failed to sync your reward. Please try again.",
            );
        }
    };

    const handleRemoveMyReward = async () => {
        showModal(
            "Confirm Removal",
            "Remove your stream team channel point reward from Twitch?",
            "confirm",
            async () => {
                try {
                    const result = await removeMyStreamTeamChannelPointReward();
                    const graphQLError = result?.errors?.[0];
                    if (graphQLError) {
                        showRewardError(
                            graphQLError,
                            "Failed to remove your reward. Please try again.",
                        );
                        return;
                    }

                    refetch();
                    showModal(
                        "Success",
                        "Your stream team channel point reward was removed.",
                        "success",
                    );
                } catch (error: any) {
                    showRewardError(
                        error?.graphQLErrors?.[0],
                        "Failed to remove your reward. Please try again.",
                    );
                }
            },
        );
    };

    const handleDownloadRunestoneForTwitch = async () => {
        try {
            const baseUrl = import.meta.env.VITE_VANDERHEIM_NINJA_API_URL;
            const response = await fetch(
                `${baseUrl}/longhouse-api/village/players/me/runestone/twitch-assets`,
                {
                    method: "GET",
                    credentials: "include",
                },
            );

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(
                    `Download failed (${response.status}): ${errorText}`,
                );
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = "twitch-runestone-icons.zip";
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Failed to download Twitch runestone zip:", error);
            showModal(
                "Download Error",
                "Could not generate Twitch icon zip from your runestone.",
                "error",
            );
        }
    };

    const initiateDiscordOAuth = async () => {
        try {
            console.debug("[Discord OAuth] Starting initiation...");

            // Get CSRF token from cookies
            const csrfToken = document.cookie
                .split("; ")
                .find((row) => row.startsWith("csrftoken="));
            console.debug("[Discord OAuth] CSRF token found:", !!csrfToken);

            const url = `${import.meta.env.VITE_VANDERHEIM_NINJA_API_URL}/longhouse-api/village/oauth/discord/initiate`;
            console.debug("[Discord OAuth] Calling URL:", url);
            console.debug(
                "[Discord OAuth] API URL Base:",
                import.meta.env.VITE_VANDERHEIM_NINJA_API_URL,
            );

            // Call backend to get OAuth URL
            const response = await fetch(url, {
                method: "GET",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    ...(csrfToken && {
                        "X-CSRFToken": csrfToken.split("=")[1],
                    }),
                },
            });

            console.debug("[Discord OAuth] Response status:", response.status);
            console.debug("[Discord OAuth] Response OK:", response.ok);
            console.debug(
                "[Discord OAuth] Response headers:",
                Object.fromEntries(response.headers.entries()),
            );

            if (!response.ok) {
                const errorText = await response.text();
                console.error(
                    "[Discord OAuth] Error response text:",
                    errorText,
                );
                throw new Error(
                    `Failed to initiate Discord OAuth: ${response.status} - ${errorText}`,
                );
            }

            const data = await response.json();
            console.debug("[Discord OAuth] Response data:", data);

            if (!data.authorization_url) {
                console.error(
                    "[Discord OAuth] No authorization_url in response",
                );
                throw new Error("No authorization URL received");
            }

            console.debug(
                "[Discord OAuth] Redirecting to:",
                data.authorization_url,
            );
            // Redirect user to Discord OAuth page
            window.location.href = data.authorization_url;
        } catch (error) {
            console.error("[Discord OAuth] Error:", error);
            console.error(
                "[Discord OAuth] Error stack:",
                error instanceof Error ? error.stack : "N/A",
            );
            showModal(
                "Authentication Error",
                `Failed to start Discord authentication: ${error instanceof Error ? error.message : "Unknown error"}`,
                "error",
            );
        }
    };

    const initiateTwitchOAuth = async () => {
        try {
            console.debug("[Twitch OAuth] Starting initiation...");

            // Get CSRF token from cookies
            const csrfToken = document.cookie
                .split("; ")
                .find((row) => row.startsWith("csrftoken="));
            console.debug("[Twitch OAuth] CSRF token found:", !!csrfToken);

            const url = `${import.meta.env.VITE_VANDERHEIM_NINJA_API_URL}/longhouse-api/village/oauth/twitch/initiate`;
            console.debug("[Twitch OAuth] Calling URL:", url);
            console.debug(
                "[Twitch OAuth] API URL Base:",
                import.meta.env.VITE_VANDERHEIM_NINJA_API_URL,
            );

            // Call backend to get OAuth URL
            const response = await fetch(url, {
                method: "GET",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    ...(csrfToken && {
                        "X-CSRFToken": csrfToken.split("=")[1],
                    }),
                },
            });

            console.debug("[Twitch OAuth] Response status:", response.status);
            console.debug("[Twitch OAuth] Response OK:", response.ok);
            console.debug(
                "[Twitch OAuth] Response headers:",
                Object.fromEntries(response.headers.entries()),
            );

            if (!response.ok) {
                const errorText = await response.text();
                console.error("[Twitch OAuth] Error response text:", errorText);
                throw new Error(
                    `Failed to initiate Twitch OAuth: ${response.status} - ${errorText}`,
                );
            }

            const data = await response.json();
            console.debug("[Twitch OAuth] Response data:", data);

            if (!data.authorization_url) {
                console.error(
                    "[Twitch OAuth] No authorization_url in response",
                );
                throw new Error("No authorization URL received");
            }

            console.debug(
                "[Twitch OAuth] Redirecting to:",
                data.authorization_url,
            );
            // Redirect user to Twitch OAuth page
            window.location.href = data.authorization_url;
        } catch (error) {
            console.error("[Twitch OAuth] Error:", error);
            console.error(
                "[Twitch OAuth] Error stack:",
                error instanceof Error ? error.stack : "N/A",
            );
            showModal(
                "Authentication Error",
                `Failed to start Twitch authentication: ${error instanceof Error ? error.message : "Unknown error"}`,
                "error",
            );
        }
    };

    const initiateCentralBotOAuth = async () => {
        try {
            console.debug("[RVNSBOT OAuth] Starting initiation...");

            const csrfToken = document.cookie
                .split("; ")
                .find((row) => row.startsWith("csrftoken="));

            const url = `${import.meta.env.VITE_VANDERHEIM_NINJA_API_URL}/longhouse-api/village/oauth/twitch/central-bot/initiate`;

            const response = await fetch(url, {
                method: "GET",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    ...(csrfToken && {
                        "X-CSRFToken": csrfToken.split("=")[1],
                    }),
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(
                    `Failed to initiate RVNSBOT OAuth: ${response.status} - ${errorText}`,
                );
            }

            const data = await response.json();

            if (!data.authorization_url) {
                throw new Error("No authorization URL received");
            }

            window.location.href = data.authorization_url;
        } catch (error) {
            console.error("[RVNSBOT OAuth] Error:", error);
            showModal(
                "Authentication Error",
                `Failed to start RVNSBOT authorization: ${error instanceof Error ? error.message : "Unknown error"}`,
                "error",
            );
        }
    };

    return (
        <div className="bg-brand_charcoal/90 min-h-screen w-full p-6">
            <div className="mx-auto">
                {/* Page Header */}
                <div className="mb-8">
                    <h1 className="font-prviking text-brand mb-2 flex items-center gap-3 text-5xl">
                        <User className="h-10 w-10" />
                        {convertLetters("My Account")}
                    </h1>
                    <p className="font-garamond text-xl text-slate-300">
                        Manage your account settings and stream team membership
                    </p>
                </div>

                {/* Two Column Layout */}
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    {/* Left Column */}
                    <div className="space-y-6">
                        {/* Discord Connection */}
                        <div className="bg-brand_charcoal/95 border-brand rounded-xl border-2 p-6 shadow-xl">
                            <h2 className="font-prviking text-brand mb-6 flex items-center gap-2 text-3xl">
                                <MessageSquare className="h-7 w-7" />
                                {convertLetters("Discord")}
                            </h2>
                            {account?.hasDiscord ? (
                                <div className="space-y-4">
                                    <div className="flex items-center gap-4 rounded-lg bg-slate-900/50 p-4">
                                        {account.discordAuth?.discordAvatar &&
                                            account.discordAuth?.discordId && (
                                                <img
                                                    src={`https://cdn.discordapp.com/avatars/${account.discordAuth.discordId}/${account.discordAuth.discordAvatar}.png`}
                                                    alt="Discord Avatar"
                                                    className="h-16 w-16 rounded-full border-4 border-indigo-500 shadow-lg"
                                                />
                                            )}
                                        <div className="flex-1">
                                            <p className="font-garamond text-xl font-bold text-white">
                                                {
                                                    account.discordAuth
                                                        ?.discordUsername
                                                }
                                                {account.discordAuth
                                                    ?.discordDiscriminator &&
                                                    `#${account.discordAuth.discordDiscriminator}`}
                                            </p>
                                            <p className="flex items-center gap-2 text-sm text-green-400">
                                                <CheckCircle className="h-4 w-4" />
                                                <span className="font-garamond font-medium">
                                                    Connected
                                                </span>
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleUnlinkDiscord}
                                        className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-red-600 bg-red-950/50 px-4 py-3 text-red-400 transition-all hover:bg-red-900/50"
                                    >
                                        <Unlink className="h-5 w-5" />
                                        <span className="font-garamond font-medium">
                                            Unlink Discord
                                        </span>
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <p className="font-garamond text-base text-slate-300">
                                        Link your Discord account to connect
                                        with the community
                                    </p>
                                    <button
                                        onClick={initiateDiscordOAuth}
                                        className="bg-brand hover:bg-brand/90 flex w-full items-center justify-center gap-2 rounded-lg px-6 py-3 font-bold text-slate-900 shadow-lg transition-all"
                                    >
                                        <LinkIcon className="h-5 w-5" />
                                        <span className="font-prviking text-lg">
                                            {convertLetters("Link Discord")}
                                        </span>
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Extension Linking */}
                        {/* <ExtensionLinkingSection /> */}

                        {/* Account Settings */}
                        <div className="bg-brand_charcoal/95 border-brand rounded-xl border-2 p-6 shadow-xl">
                            <h2 className="font-prviking text-brand mb-4 flex items-center gap-2 text-3xl">
                                <Settings className="h-7 w-7" />
                                {convertLetters("Settings")}
                            </h2>
                            <p className="font-garamond text-base text-slate-400">
                                Additional account settings coming soon...
                            </p>
                        </div>
                    </div>

                    {/* Right Column - Stream Team */}
                    <div className="bg-brand_charcoal/95 border-brand rounded-xl border-2 p-6 shadow-xl">
                        <h2 className="font-prviking text-brand mb-6 flex items-center gap-2 text-3xl">
                            <TwitchIcon className="h-7 w-7" />
                            {convertLetters("Stream Team")}
                        </h2>

                        {/* Pending Invite */}
                        {account?.pendingStreamInvite && (
                            <div className="mb-6 rounded-xl border-2 border-yellow-500 bg-yellow-950/30 p-6">
                                <div className="flex items-start gap-4">
                                    <Clock className="mt-1 h-7 w-7 text-yellow-400" />
                                    <div className="flex-1">
                                        <p className="font-prviking text-2xl text-yellow-300">
                                            {convertLetters("Invitation")}
                                        </p>
                                        <p className="font-garamond mt-2 text-base text-slate-200">
                                            You've been invited by{" "}
                                            <span className="font-bold text-white">
                                                {
                                                    account.pendingStreamInvite
                                                        .invitedBy.name
                                                }
                                            </span>
                                        </p>
                                        {account.pendingStreamInvite
                                            .message && (
                                            <p className="font-garamond mt-2 text-sm text-slate-300 italic">
                                                "
                                                {
                                                    account.pendingStreamInvite
                                                        .message
                                                }
                                                "
                                            </p>
                                        )}
                                        <div className="mt-4 flex gap-3">
                                            <button
                                                onClick={() =>
                                                    handleRespondToInvite(true)
                                                }
                                                className="font-prviking flex-1 rounded-lg border-2 border-green-500 bg-green-950/30 px-6 py-2 text-green-400 transition-all hover:bg-green-900/50"
                                            >
                                                {convertLetters("Accept")}
                                            </button>
                                            <button
                                                onClick={() =>
                                                    handleRespondToInvite(false)
                                                }
                                                className="font-prviking flex-1 rounded-lg border-2 border-red-500 bg-red-950/30 px-6 py-2 text-red-400 transition-all hover:bg-red-900/50"
                                            >
                                                {convertLetters("Decline")}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Pending Request */}
                        {account?.pendingStreamRequest && (
                            <div className="mb-6 rounded-xl border-2 border-blue-500 bg-blue-950/30 p-6">
                                <div className="flex items-start gap-4">
                                    <Clock className="mt-1 h-7 w-7 text-blue-400" />
                                    <div>
                                        <p className="font-prviking text-2xl text-blue-300">
                                            {convertLetters("Pending Review")}
                                        </p>
                                        <p className="font-garamond mt-2 text-base text-slate-200">
                                            Your application to join the{" "}
                                            {account.pendingStreamRequest
                                                .isAcademyApplication
                                                ? "VRX Academy"
                                                : "RAVENGUARD"}{" "}
                                            is being reviewed by administrators.
                                        </p>
                                        <p className="font-garamond mt-2 text-sm text-slate-400">
                                            Submitted:{" "}
                                            {new Date(
                                                account.pendingStreamRequest
                                                    .createdAt,
                                            ).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Stream Team Member */}
                        {account?.isStreamTeam ? (
                            <div className="space-y-6">
                                <div className="rounded-lg bg-slate-900/50 p-4">
                                    {isAcademyMember ? (
                                        <>
                                            <p className="font-prviking flex items-center gap-2 text-2xl text-blue-300">
                                                {convertLetters(
                                                    "VRX Academy Member",
                                                )}
                                            </p>
                                            <p className="font-garamond mt-1 text-sm text-slate-300">
                                                You are currently in the VRX
                                                Academy.
                                            </p>
                                        </>
                                    ) : (
                                        <p className="font-prviking flex items-center gap-2 text-2xl text-green-400">
                                            <CheckCircle className="h-6 w-6" />
                                            {convertLetters("Active Member")}
                                        </p>
                                    )}
                                    <p className="font-garamond mt-1 text-sm text-slate-400">
                                        Member since:{" "}
                                        {new Date(
                                            account.streamTeamMember.joinedAt,
                                        ).toLocaleDateString()}
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                                    {/* Bio Section */}
                                    <div className="rounded-lg border-2 border-slate-700 bg-slate-900/50 p-4">
                                        <div className="mb-3 flex items-center justify-between">
                                            <label className="font-prviking text-brand text-xl">
                                                {convertLetters("Bio")}
                                            </label>
                                            <button
                                                onClick={() => {
                                                    setBio(
                                                        account.streamTeamMember
                                                            ?.bio || "",
                                                    );
                                                    setShowBioEditor(
                                                        !showBioEditor,
                                                    );
                                                }}
                                                className="font-garamond text-sm font-medium text-blue-400 hover:text-blue-300"
                                            >
                                                {showBioEditor
                                                    ? "Cancel"
                                                    : "Edit"}
                                            </button>
                                        </div>
                                        {showBioEditor ? (
                                            <div className="space-y-3">
                                                <textarea
                                                    value={bio}
                                                    onChange={(e) =>
                                                        setBio(
                                                            e.target.value.slice(
                                                                0,
                                                                STREAM_TEAM_BIO_MAX_LENGTH,
                                                            ),
                                                        )
                                                    }
                                                    maxLength={
                                                        STREAM_TEAM_BIO_MAX_LENGTH
                                                    }
                                                    className="font-garamond w-full rounded-lg border-2 border-slate-600 bg-slate-950 px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                                                    rows={4}
                                                    placeholder="Tell viewers about yourself..."
                                                />
                                                <p className="font-garamond text-right text-xs text-slate-400">
                                                    {bio.length}/
                                                    {STREAM_TEAM_BIO_MAX_LENGTH}
                                                </p>
                                                <button
                                                    onClick={handleUpdateBio}
                                                    className="bg-brand hover:bg-brand/90 font-prviking rounded-lg px-6 py-2 text-slate-900 shadow-md transition-all"
                                                >
                                                    {convertLetters("Save Bio")}
                                                </button>
                                            </div>
                                        ) : (
                                            <p className="font-garamond text-base text-slate-300">
                                                {account.streamTeamMember
                                                    ?.bio || "No bio set"}
                                            </p>
                                        )}
                                    </div>

                                    {isAcademyMember &&
                                    !data?.featureFlags
                                        ?.academyRunestoneSystemEnabled ? null : (
                                        <div className="rounded-lg border-2 border-slate-700 bg-slate-900/50 p-4">
                                            <h3 className="font-prviking text-brand mb-3 text-xl">
                                                {convertLetters(
                                                    isAcademyMember
                                                        ? "Your Runestone"
                                                        : "Runestone Carving",
                                                )}
                                            </h3>
                                            {account.hasRunestone &&
                                            account.runestones?.length > 0 ? (
                                                <div className="flex flex-col items-center gap-4">
                                                    {(isAcademyMember
                                                        ? account.runestones.slice(
                                                              0,
                                                              1,
                                                          )
                                                        : account.runestones
                                                    ).map(
                                                        (runestone: {
                                                            id: string;
                                                            imageUrl: string;
                                                            runeName: string;
                                                            createdAt: string;
                                                        }) => (
                                                            <div
                                                                key={
                                                                    runestone.id
                                                                }
                                                                className="flex flex-col items-center gap-3"
                                                            >
                                                                {runestone.imageUrl && (
                                                                    <img
                                                                        src={
                                                                            runestone.imageUrl
                                                                        }
                                                                        alt={`${runestone.runeName} Runestone`}
                                                                        className="h-52 w-52 rounded-lg border-2 border-slate-600 object-contain"
                                                                    />
                                                                )}
                                                                <div className="text-center">
                                                                    <p className="font-prviking text-brand text-xl">
                                                                        {convertLetters(
                                                                            runestone.runeName,
                                                                        )}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        ),
                                                    )}
                                                    <button
                                                        onClick={
                                                            handleDownloadRunestoneForTwitch
                                                        }
                                                        className="font-prviking inline-flex items-center gap-2 rounded-lg border-2 border-slate-600 bg-slate-950 px-5 py-2 text-slate-200 transition-all hover:bg-slate-900"
                                                    >
                                                        <Download className="h-4 w-4" />
                                                        {convertLetters(
                                                            "Download for Twitch",
                                                        )}
                                                    </button>
                                                    <p className="font-garamond text-center text-xs text-slate-400">
                                                        Includes transparent
                                                        PNGs at 28x28, 56x56,
                                                        and 112x112.
                                                    </p>
                                                </div>
                                            ) : (
                                                <>
                                                    <p className="font-garamond mb-4 text-sm text-slate-300">
                                                        {isAcademyMember
                                                            ? "Academy members share the standard VRX Academy Runestone. It is assigned to you when you enable your channel point reward below."
                                                            : "As a stream team member, you can carve your own personalised runestone."}
                                                    </p>
                                                    {!isAcademyMember && (
                                                        <a
                                                            href="/runestone-carving"
                                                            className="font-prviking bg-brand hover:bg-brand/90 inline-block rounded-lg px-6 py-2 text-slate-900 shadow-md transition-all"
                                                        >
                                                            {convertLetters(
                                                                "Carve a Runestone",
                                                            )}
                                                        </a>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {/* end Bio/Runestone grid */}

                                {/* Social Links Section */}
                                <div className="rounded-lg border-2 border-slate-700 bg-slate-900/50 p-4">
                                    <div className="mb-3 flex items-center justify-between">
                                        <label className="font-prviking text-brand text-xl">
                                            {convertLetters("Social Links")}
                                        </label>
                                        <button
                                            onClick={() => {
                                                if (!showSocialLinksEditor) {
                                                    setTwitterUrl(
                                                        account.streamTeamMember
                                                            ?.twitterUrl || "",
                                                    );
                                                    setYoutubeUrl(
                                                        account.streamTeamMember
                                                            ?.youtubeUrl || "",
                                                    );
                                                    setDiscordUrl(
                                                        account.streamTeamMember
                                                            ?.discordUrl || "",
                                                    );
                                                    setInstagramUrl(
                                                        account.streamTeamMember
                                                            ?.instagramUrl ||
                                                            "",
                                                    );
                                                    setTiktokUrl(
                                                        account.streamTeamMember
                                                            ?.tiktokUrl || "",
                                                    );
                                                }
                                                setShowSocialLinksEditor(
                                                    !showSocialLinksEditor,
                                                );
                                            }}
                                            className="font-garamond text-sm font-medium text-blue-400 hover:text-blue-300"
                                        >
                                            {showSocialLinksEditor
                                                ? "Cancel"
                                                : "Edit"}
                                        </button>
                                    </div>
                                    {showSocialLinksEditor ? (
                                        <div className="space-y-3">
                                            <div>
                                                <label className="font-garamond mb-1 block text-sm text-slate-400">
                                                    Twitter/X URL
                                                </label>
                                                <input
                                                    type="url"
                                                    value={twitterUrl}
                                                    onChange={(e) =>
                                                        setTwitterUrl(
                                                            e.target.value,
                                                        )
                                                    }
                                                    placeholder="https://twitter.com/username"
                                                    className="font-garamond w-full rounded-lg border-2 border-slate-600 bg-slate-950 px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                                                />
                                            </div>
                                            <div>
                                                <label className="font-garamond mb-1 block text-sm text-slate-400">
                                                    YouTube URL
                                                </label>
                                                <input
                                                    type="url"
                                                    value={youtubeUrl}
                                                    onChange={(e) =>
                                                        setYoutubeUrl(
                                                            e.target.value,
                                                        )
                                                    }
                                                    placeholder="https://youtube.com/@username"
                                                    className="font-garamond w-full rounded-lg border-2 border-slate-600 bg-slate-950 px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                                                />
                                            </div>
                                            <div>
                                                <label className="font-garamond mb-1 block text-sm text-slate-400">
                                                    Discord URL
                                                </label>
                                                <input
                                                    type="url"
                                                    value={discordUrl}
                                                    onChange={(e) =>
                                                        setDiscordUrl(
                                                            e.target.value,
                                                        )
                                                    }
                                                    placeholder="https://discord.gg/invitecode"
                                                    className="font-garamond w-full rounded-lg border-2 border-slate-600 bg-slate-950 px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                                                />
                                            </div>
                                            <div>
                                                <label className="font-garamond mb-1 block text-sm text-slate-400">
                                                    Instagram URL
                                                </label>
                                                <input
                                                    type="url"
                                                    value={instagramUrl}
                                                    onChange={(e) =>
                                                        setInstagramUrl(
                                                            e.target.value,
                                                        )
                                                    }
                                                    placeholder="https://instagram.com/username"
                                                    className="font-garamond w-full rounded-lg border-2 border-slate-600 bg-slate-950 px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                                                />
                                            </div>
                                            <div>
                                                <label className="font-garamond mb-1 block text-sm text-slate-400">
                                                    TikTok URL
                                                </label>
                                                <input
                                                    type="url"
                                                    value={tiktokUrl}
                                                    onChange={(e) =>
                                                        setTiktokUrl(
                                                            e.target.value,
                                                        )
                                                    }
                                                    placeholder="https://tiktok.com/@username"
                                                    className="font-garamond w-full rounded-lg border-2 border-slate-600 bg-slate-950 px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                                                />
                                            </div>
                                            <button
                                                onClick={handleUpdateBio}
                                                className="bg-brand hover:bg-brand/90 font-prviking rounded-lg px-6 py-2 text-slate-900 shadow-md transition-all"
                                            >
                                                {convertLetters(
                                                    "Save Social Links",
                                                )}
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="font-garamond space-y-2 text-sm text-slate-300">
                                            {account.streamTeamMember
                                                ?.twitterUrl && (
                                                <p>
                                                    Twitter:{" "}
                                                    {
                                                        account.streamTeamMember
                                                            .twitterUrl
                                                    }
                                                </p>
                                            )}
                                            {account.streamTeamMember
                                                ?.youtubeUrl && (
                                                <p>
                                                    YouTube:{" "}
                                                    {
                                                        account.streamTeamMember
                                                            .youtubeUrl
                                                    }
                                                </p>
                                            )}
                                            {account.streamTeamMember
                                                ?.discordUrl && (
                                                <p>
                                                    Discord:{" "}
                                                    {
                                                        account.streamTeamMember
                                                            .discordUrl
                                                    }
                                                </p>
                                            )}
                                            {account.streamTeamMember
                                                ?.instagramUrl && (
                                                <p>
                                                    Instagram:{" "}
                                                    {
                                                        account.streamTeamMember
                                                            .instagramUrl
                                                    }
                                                </p>
                                            )}
                                            {account.streamTeamMember
                                                ?.tiktokUrl && (
                                                <p>
                                                    TikTok:{" "}
                                                    {
                                                        account.streamTeamMember
                                                            .tiktokUrl
                                                    }
                                                </p>
                                            )}
                                            {!account.streamTeamMember
                                                ?.twitterUrl &&
                                                !account.streamTeamMember
                                                    ?.youtubeUrl &&
                                                !account.streamTeamMember
                                                    ?.discordUrl &&
                                                !account.streamTeamMember
                                                    ?.instagramUrl &&
                                                !account.streamTeamMember
                                                    ?.tiktokUrl && (
                                                    <p className="text-slate-400">
                                                        No social links set
                                                    </p>
                                                )}
                                        </div>
                                    )}
                                </div>

                                {/* Twitch Auth Section */}
                                <div className="rounded-lg border-2 border-slate-700 bg-slate-900/50 p-4">
                                    <h3 className="font-prviking text-brand mb-4 text-xl">
                                        {convertLetters("Twitch Integrations")}
                                    </h3>
                                    {account.streamTeamMember?.hasTwitchAuth ? (
                                        <div className="space-y-3">
                                            <div className="rounded-lg bg-green-950/30 p-3">
                                                <p className="flex items-center gap-2 text-sm font-medium text-green-400">
                                                    <CheckCircle className="h-5 w-5" />
                                                    <span className="font-garamond">
                                                        Connected to Twitch
                                                    </span>
                                                </p>
                                                <p className="font-garamond mt-1 text-xs text-slate-400">
                                                    Your Twitch account is
                                                    connected for stream-team
                                                    features
                                                </p>
                                            </div>
                                            <button
                                                onClick={initiateTwitchOAuth}
                                                className="font-prviking w-full rounded-lg border-2 border-slate-600 bg-slate-950 px-4 py-2 text-slate-300 transition-all hover:bg-slate-900"
                                            >
                                                {convertLetters("Reconnect")}
                                            </button>
                                            <div className="rounded-lg border border-slate-700 bg-slate-950/50 p-3">
                                                <p className="font-garamond text-sm text-slate-300">
                                                    RVNSBOT authorization:{" "}
                                                    <span className="font-semibold text-white">
                                                        {account
                                                            .streamTeamMember
                                                            ?.hasCentralBotAuth
                                                            ? "Enabled"
                                                            : "Not granted"}
                                                    </span>
                                                </p>
                                                <p className="font-garamond mt-1 text-xs text-slate-400">
                                                    If enabled, chat notices are
                                                    sent by RVNSBOT. If not, we
                                                    send from your account
                                                    instead.
                                                </p>
                                                <button
                                                    onClick={
                                                        initiateCentralBotOAuth
                                                    }
                                                    className="font-prviking mt-3 w-full rounded-lg border-2 border-cyan-700 bg-cyan-950/40 px-4 py-2 text-cyan-300 transition-all hover:bg-cyan-900/50"
                                                >
                                                    {convertLetters(
                                                        account.streamTeamMember
                                                            ?.hasCentralBotAuth
                                                            ? "Reauthorize RVNSBOT"
                                                            : "Authorize RVNSBOT",
                                                    )}
                                                </button>
                                            </div>
                                            {data?.featureFlags
                                                ?.streamTeamChannelPointRewardEnabled &&
                                            !(
                                                isAcademyMember &&
                                                !data?.featureFlags
                                                    ?.academyRunestoneSystemEnabled
                                            ) ? (
                                                <div className="rounded-lg border border-slate-700 bg-slate-950/50 p-3">
                                                    <p className="font-garamond text-sm text-slate-300">
                                                        Reward status:{" "}
                                                        <span className="font-semibold text-white">
                                                            {account
                                                                .streamTeamMember
                                                                ?.twitchAuth
                                                                ?.streamTeamRewardEnabled
                                                                ? "Enabled"
                                                                : "Not configured"}
                                                        </span>
                                                    </p>
                                                    {isAcademyMember && (
                                                        <p className="font-garamond mt-1 text-xs text-slate-400">
                                                            Adding the reward
                                                            also assigns you the
                                                            VRX Academy
                                                            Runestone.
                                                        </p>
                                                    )}
                                                    <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
                                                        <button
                                                            onClick={
                                                                handleSyncMyReward
                                                            }
                                                            className="font-prviking rounded-lg border-2 border-blue-600 bg-blue-950/40 px-4 py-2 text-blue-300 transition-all hover:bg-blue-900/50"
                                                        >
                                                            {convertLetters(
                                                                account
                                                                    .streamTeamMember
                                                                    ?.twitchAuth
                                                                    ?.streamTeamRewardEnabled
                                                                    ? "Update Reward"
                                                                    : "Add Reward",
                                                            )}
                                                        </button>
                                                        <button
                                                            onClick={
                                                                handleRemoveMyReward
                                                            }
                                                            disabled={
                                                                !account
                                                                    .streamTeamMember
                                                                    ?.twitchAuth
                                                                    ?.streamTeamRewardEnabled
                                                            }
                                                            className="font-prviking rounded-lg border-2 border-red-700 bg-red-950/40 px-4 py-2 text-red-300 transition-all enabled:hover:bg-red-900/50 disabled:cursor-not-allowed disabled:opacity-50"
                                                        >
                                                            {convertLetters(
                                                                "Remove Reward",
                                                            )}
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : null}
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            <p className="font-garamond text-sm text-slate-300">
                                                Connect Twitch for stream-team
                                                features (schedule sync,
                                                rewards, and notices)
                                            </p>
                                            <button
                                                onClick={initiateTwitchOAuth}
                                                className="flex w-full items-center justify-center gap-2 rounded-lg bg-purple-600 px-6 py-3 text-white shadow-lg transition-all hover:bg-purple-700"
                                            >
                                                <TwitchIcon className="h-5 w-5" />
                                                <span className="font-prviking text-lg">
                                                    {convertLetters(
                                                        "Connect Twitch",
                                                    )}
                                                </span>
                                            </button>
                                            <div className="rounded-lg border border-slate-700 bg-slate-950/50 p-3">
                                                <p className="font-garamond text-sm text-slate-300">
                                                    You can also authorize the
                                                    RVNSBOT account to post
                                                    stream-team notices in your
                                                    chat.
                                                </p>
                                                <button
                                                    onClick={
                                                        initiateCentralBotOAuth
                                                    }
                                                    className="font-prviking mt-3 w-full rounded-lg border-2 border-cyan-700 bg-cyan-950/40 px-4 py-2 text-cyan-300 transition-all hover:bg-cyan-900/50"
                                                >
                                                    {convertLetters(
                                                        "Authorize RVNSBOT",
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Stream Team Events Section */}
                                {account.streamTeamMember?.canCreateEvents &&
                                    !isAcademyMember && (
                                        <div className="rounded-lg border-2 border-slate-700 bg-slate-900/50 p-4">
                                            <div className="mb-4 flex items-center justify-between">
                                                <h3 className="font-prviking text-brand text-xl">
                                                    {convertLetters(
                                                        "Your Events",
                                                    )}
                                                </h3>
                                                <button
                                                    onClick={() => {
                                                        resetEventForm();
                                                        setShowEventForm(true);
                                                    }}
                                                    className="bg-brand hover:bg-brand/90 font-prviking rounded-lg px-4 py-2 text-sm text-slate-900 transition-all"
                                                >
                                                    {convertLetters(
                                                        "Create Event",
                                                    )}
                                                </button>
                                            </div>

                                            {eventsLoading ? (
                                                <div className="flex justify-center py-4">
                                                    <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                                                </div>
                                            ) : eventsData?.myStreamTeamEvents
                                                  ?.length > 0 ? (
                                                <div className="space-y-3">
                                                    {eventsData.myStreamTeamEvents.map(
                                                        (event: any) => (
                                                            <div
                                                                key={event.id}
                                                                className="rounded-lg border border-slate-700 bg-slate-950/50 p-3"
                                                            >
                                                                <div className="flex items-start justify-between gap-2">
                                                                    <div className="min-w-0 flex-1">
                                                                        <h4 className="font-prviking truncate text-base text-white">
                                                                            {
                                                                                event.title
                                                                            }
                                                                        </h4>
                                                                        <div className="mt-1 flex flex-wrap gap-2">
                                                                            <span
                                                                                className={`rounded px-2 py-0.5 text-xs font-medium ${
                                                                                    event.status ===
                                                                                    "live"
                                                                                        ? "bg-red-500 text-white"
                                                                                        : event.status ===
                                                                                            "scheduled"
                                                                                          ? "bg-blue-600 text-white"
                                                                                          : event.status ===
                                                                                              "completed"
                                                                                            ? "bg-green-600 text-white"
                                                                                            : event.status ===
                                                                                                "cancelled"
                                                                                              ? "bg-gray-600 text-white"
                                                                                              : "bg-yellow-600 text-black"
                                                                                }`}
                                                                            >
                                                                                {event.status.toUpperCase()}
                                                                            </span>
                                                                            <span className="rounded bg-slate-700 px-2 py-0.5 text-xs text-slate-300">
                                                                                {event.eventType.replace(
                                                                                    "_",
                                                                                    " ",
                                                                                )}
                                                                            </span>
                                                                        </div>
                                                                        <p className="font-garamond mt-1 text-xs text-slate-400">
                                                                            {new Date(
                                                                                event.startTime,
                                                                            ).toLocaleDateString(
                                                                                undefined,
                                                                                {
                                                                                    weekday:
                                                                                        "short",
                                                                                    month: "short",
                                                                                    day: "numeric",
                                                                                    hour: "2-digit",
                                                                                    minute: "2-digit",
                                                                                },
                                                                            )}
                                                                        </p>
                                                                    </div>
                                                                    <div className="flex gap-1">
                                                                        <button
                                                                            onClick={() =>
                                                                                openEditEventForm(
                                                                                    event,
                                                                                )
                                                                            }
                                                                            className="rounded p-1.5 text-slate-400 transition-colors hover:bg-slate-800 hover:text-white"
                                                                            title="Edit event"
                                                                        >
                                                                            <Edit2 className="h-4 w-4" />
                                                                        </button>
                                                                        <button
                                                                            onClick={() =>
                                                                                handleDeleteEvent(
                                                                                    event.id,
                                                                                    event.title,
                                                                                )
                                                                            }
                                                                            className="rounded p-1.5 text-slate-400 transition-colors hover:bg-red-900/50 hover:text-red-400"
                                                                            title="Delete event"
                                                                        >
                                                                            <Trash2 className="h-4 w-4" />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ),
                                                    )}
                                                </div>
                                            ) : (
                                                <p className="font-garamond text-sm text-slate-400">
                                                    You haven't created any
                                                    events yet. Create one to
                                                    get started!
                                                </p>
                                            )}
                                        </div>
                                    )}
                            </div>
                        ) : account?.canRequestStreamTeam ? (
                            /* Request to Join */
                            requestForm ? (
                                <div className="space-y-4">
                                    <div
                                        className={`rounded-lg border-2 p-3 ${
                                            requestForm === "academy"
                                                ? "border-blue-600 bg-blue-950/30"
                                                : "border-brand bg-slate-900/50"
                                        }`}
                                    >
                                        <p className="font-prviking text-lg text-slate-100">
                                            {convertLetters(
                                                requestForm === "academy"
                                                    ? "Applying to: THE VRX ACADEMY"
                                                    : "Applying to: THE RAVENGUARD",
                                            )}
                                        </p>
                                        <p className="font-garamond mt-1 text-sm text-slate-400">
                                            {requestForm === "academy"
                                                ? "The Academy is for up-and-coming streamers building their craft. Academy members share the standard VRX Academy Runestone."
                                                : "The RAVENGUARD is the full Vanderheim stream team, where you carve your own personal Runestone."}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="font-prviking text-brand mb-2 block text-xl">
                                            {convertLetters(
                                                requestForm === "academy"
                                                    ? "Why join the VRX Academy?"
                                                    : "Why join the stream team?",
                                            )}
                                        </label>
                                        <textarea
                                            value={requestMessage}
                                            onChange={(e) =>
                                                setRequestMessage(
                                                    e.target.value,
                                                )
                                            }
                                            className="font-garamond w-full rounded-lg border-2 border-slate-600 bg-slate-950 px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                                            rows={6}
                                            placeholder="Tell us about your streaming experience, schedule, and why you'd be a good fit..."
                                        />
                                    </div>
                                    <div className="flex gap-3">
                                        <button
                                            onClick={handleRequestStreamTeam}
                                            className="bg-brand hover:bg-brand/90 font-prviking flex-1 rounded-lg px-6 py-3 text-slate-900 shadow-lg transition-all"
                                        >
                                            {convertLetters("Submit")}
                                        </button>
                                        <button
                                            onClick={() => {
                                                setRequestForm(null);
                                                setRequestMessage("");
                                            }}
                                            className="font-prviking flex-1 rounded-lg border-2 border-slate-600 bg-slate-900 px-6 py-3 text-slate-300 transition-all hover:bg-slate-800"
                                        >
                                            {convertLetters("Cancel")}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {data?.streamSettings
                                        ?.acceptingApplications === false &&
                                    data?.streamSettings
                                        ?.acceptingAcademyApplications ===
                                        false ? (
                                        <div className="rounded-lg border-2 border-yellow-600 bg-yellow-900/20 p-4">
                                            <p className="font-garamond text-base text-yellow-400">
                                                We are not currently accepting
                                                RAVENGUARD or VRX Academy
                                                applications. Please check back
                                                later or wait for an invitation.
                                            </p>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="space-y-2">
                                                <p className="font-garamond text-base text-slate-300">
                                                    Join the RAVENGUARD to have
                                                    your streams featured on the
                                                    community page and carve
                                                    your own Runestone.
                                                </p>
                                                {data?.streamSettings
                                                    ?.acceptingApplications ===
                                                false ? (
                                                    <p className="font-garamond rounded-lg border-2 border-yellow-600 bg-yellow-900/20 p-3 text-sm text-yellow-400">
                                                        RAVENGUARD applications
                                                        are currently closed.
                                                    </p>
                                                ) : (
                                                    <button
                                                        onClick={() =>
                                                            setRequestForm(
                                                                "ravenguard",
                                                            )
                                                        }
                                                        className="bg-brand hover:bg-brand/90 font-prviking w-full rounded-lg px-6 py-3 text-slate-900 shadow-lg transition-all"
                                                    >
                                                        {convertLetters(
                                                            "Apply to the RAVENGUARD",
                                                        )}
                                                    </button>
                                                )}
                                            </div>

                                            <div className="space-y-2 border-t-2 border-slate-700 pt-4">
                                                <p className="font-garamond text-base text-slate-300">
                                                    Not quite ready? Apply to
                                                    the VRX Academy instead and
                                                    level up your craft with
                                                    support from the team.
                                                </p>
                                                {data?.streamSettings
                                                    ?.acceptingAcademyApplications ===
                                                false ? (
                                                    <p className="font-garamond rounded-lg border-2 border-yellow-600 bg-yellow-900/20 p-3 text-sm text-yellow-400">
                                                        VRX Academy applications
                                                        are currently closed.
                                                    </p>
                                                ) : (
                                                    <button
                                                        onClick={() =>
                                                            setRequestForm(
                                                                "academy",
                                                            )
                                                        }
                                                        className="font-prviking w-full rounded-lg border-2 border-blue-600 bg-blue-950/40 px-6 py-3 text-blue-300 shadow-lg transition-all hover:bg-blue-900/50"
                                                    >
                                                        {convertLetters(
                                                            "Apply to the VRX Academy",
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            )
                        ) : (
                            <div className="rounded-lg bg-slate-900/50 p-4">
                                <p className="font-garamond text-base text-slate-400">
                                    {account?.pendingStreamRequest
                                        ? "You have a pending application. Please wait for a response from administrators."
                                        : account?.pendingStreamInvite
                                          ? "You have a pending invitation. Please respond to it above."
                                          : "Unable to request stream team membership at this time. Please contact an administrator if you believe this is an error."}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Event Form Modal */}
            {showEventForm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
                    <div className="bg-brand_charcoal max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl border-2 border-slate-700 p-6 shadow-xl">
                        <div className="mb-4 flex items-center justify-between">
                            <h3 className="font-prviking text-brand text-2xl">
                                {convertLetters(
                                    editingEvent
                                        ? "Edit Event"
                                        : "Create Event",
                                )}
                            </h3>
                            <span className="font-garamond rounded bg-slate-800 px-2 py-1 text-xs text-slate-400">
                                Timezone: {userTimezone}
                            </span>
                        </div>
                        <div className="grid gap-4 md:grid-cols-2">
                            {/* Left Column */}
                            <div className="space-y-4">
                                <div>
                                    <label className="font-garamond mb-1 block text-sm text-slate-400">
                                        Title *
                                    </label>
                                    <input
                                        type="text"
                                        value={eventTitle}
                                        onChange={(e) =>
                                            setEventTitle(e.target.value)
                                        }
                                        className="font-garamond w-full rounded-lg border-2 border-slate-600 bg-slate-950 px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                                        placeholder="Event title"
                                    />
                                </div>
                                <div>
                                    <label className="font-garamond mb-1 block text-sm text-slate-400">
                                        Summary
                                    </label>
                                    <input
                                        type="text"
                                        value={eventSummary}
                                        onChange={(e) =>
                                            setEventSummary(e.target.value)
                                        }
                                        className="font-garamond w-full rounded-lg border-2 border-slate-600 bg-slate-950 px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                                        placeholder="Brief summary"
                                    />
                                </div>
                                <div>
                                    <label className="font-garamond mb-1 block text-sm text-slate-400">
                                        Description
                                    </label>
                                    <textarea
                                        value={eventDescription}
                                        onChange={(e) =>
                                            setEventDescription(e.target.value)
                                        }
                                        rows={4}
                                        className="font-garamond w-full rounded-lg border-2 border-slate-600 bg-slate-950 px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                                        placeholder="Event description..."
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="font-garamond mb-1 block text-sm text-slate-400">
                                            Event Type
                                        </label>
                                        <select
                                            value={eventType}
                                            onChange={(e) =>
                                                setEventType(e.target.value)
                                            }
                                            className="font-garamond w-full rounded-lg border-2 border-slate-600 bg-slate-950 px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                                        >
                                            <option value="community">
                                                Community
                                            </option>
                                            <option value="subathon">
                                                Subathon
                                            </option>
                                            <option value="charity">
                                                Charity
                                            </option>
                                            <option value="competition">
                                                Competition
                                            </option>
                                            <option value="tournament">
                                                Tournament
                                            </option>
                                            <option value="collaboration">
                                                Collaboration
                                            </option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="font-garamond mb-1 block text-sm text-slate-400">
                                            Status
                                        </label>
                                        <select
                                            value={eventStatus}
                                            onChange={(e) =>
                                                setEventStatus(e.target.value)
                                            }
                                            className="font-garamond w-full rounded-lg border-2 border-slate-600 bg-slate-950 px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                                        >
                                            <option value="draft">Draft</option>
                                            <option value="scheduled">
                                                Scheduled
                                            </option>
                                            <option value="live">Live</option>
                                            <option value="completed">
                                                Completed
                                            </option>
                                            <option value="cancelled">
                                                Cancelled
                                            </option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Right Column */}
                            <div className="space-y-4">
                                <div>
                                    <label className="font-garamond mb-1 block text-sm text-slate-400">
                                        Start Time *
                                    </label>
                                    <input
                                        type="datetime-local"
                                        value={eventStartTime}
                                        onChange={(e) =>
                                            setEventStartTime(e.target.value)
                                        }
                                        className="font-garamond w-full rounded-lg border-2 border-slate-600 bg-slate-950 px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="font-garamond mb-1 block text-sm text-slate-400">
                                        End Time
                                    </label>
                                    <input
                                        type="datetime-local"
                                        value={eventEndTime}
                                        onChange={(e) =>
                                            setEventEndTime(e.target.value)
                                        }
                                        className="font-garamond w-full rounded-lg border-2 border-slate-600 bg-slate-950 px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="font-garamond mb-1 block text-sm text-slate-400">
                                            Location Type
                                        </label>
                                        <select
                                            value={eventLocationType}
                                            onChange={(e) =>
                                                setEventLocationType(
                                                    e.target.value,
                                                )
                                            }
                                            className="font-garamond w-full rounded-lg border-2 border-slate-600 bg-slate-950 px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                                        >
                                            <option value="online">
                                                Online
                                            </option>
                                            <option value="in_person">
                                                In Person
                                            </option>
                                            <option value="hybrid">
                                                Hybrid
                                            </option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="font-garamond mb-1 block text-sm text-slate-400">
                                            Location Details
                                        </label>
                                        <input
                                            type="text"
                                            value={eventLocationDetails}
                                            onChange={(e) =>
                                                setEventLocationDetails(
                                                    e.target.value,
                                                )
                                            }
                                            className="font-garamond w-full rounded-lg border-2 border-slate-600 bg-slate-950 px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                                            placeholder="e.g., Twitch stream"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="font-garamond mb-1 block text-sm text-slate-400">
                                        Join URL
                                    </label>
                                    <input
                                        type="url"
                                        value={eventJoinUrl}
                                        onChange={(e) =>
                                            setEventJoinUrl(e.target.value)
                                        }
                                        className="font-garamond w-full rounded-lg border-2 border-slate-600 bg-slate-950 px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                                        placeholder="https://..."
                                    />
                                </div>
                                <div>
                                    <label className="font-garamond mb-1 block text-sm text-slate-400">
                                        Join Instructions
                                    </label>
                                    <textarea
                                        value={eventJoinInstructions}
                                        onChange={(e) =>
                                            setEventJoinInstructions(
                                                e.target.value,
                                            )
                                        }
                                        rows={3}
                                        className="font-garamond w-full rounded-lg border-2 border-slate-600 bg-slate-950 px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                                        placeholder="Instructions for joining..."
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="mt-6 flex gap-3">
                            <button
                                onClick={handleCreateOrUpdateEvent}
                                className="bg-brand hover:bg-brand/90 font-prviking flex-1 rounded-lg px-6 py-3 text-slate-900 shadow-lg transition-all"
                            >
                                {convertLetters(
                                    editingEvent ? "Update" : "Create",
                                )}
                            </button>
                            <button
                                onClick={() => {
                                    setShowEventForm(false);
                                    resetEventForm();
                                }}
                                className="font-prviking flex-1 rounded-lg border-2 border-slate-600 bg-slate-900 px-6 py-3 text-slate-300 transition-all hover:bg-slate-800"
                            >
                                {convertLetters("Cancel")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal */}
            <VillageModal
                isOpen={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                }}
                onConfirm={modalConfig.onConfirm}
                title={modalConfig.title}
                message={modalConfig.message}
                type={modalConfig.type}
            />
        </div>
    );
}

function ExtensionLinkingSection() {
    const [token, setToken] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);

    const [generateToken, { loading, error }] = useMutation(
        GENERATE_LINKING_TOKEN,
        {
            onCompleted: (data) => {
                setToken(data.generateLinkingToken.token);
            },
        },
    );

    const copyToClipboard = async () => {
        if (token) {
            await navigator.clipboard.writeText(token);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    return (
        <div className="bg-brand_charcoal/95 border-brand rounded-xl border-2 p-6 shadow-xl">
            <h2 className="font-prviking text-brand mb-4 flex items-center gap-2 text-3xl">
                <TwitchIcon className="h-7 w-7" />
                {convertLetters("Twitch Extension")}
            </h2>
            <p className="font-garamond mb-4 text-base text-slate-300">
                Link your account to the Vanderheim Twitch extension
            </p>

            {!token ? (
                <button
                    onClick={() => generateToken()}
                    disabled={loading}
                    className="bg-brand hover:bg-brand/90 flex w-full items-center justify-center gap-2 rounded-lg px-6 py-3 font-bold text-slate-900 shadow-lg transition-all disabled:opacity-50"
                >
                    <LinkIcon className="h-5 w-5" />
                    <span className="font-prviking text-lg">
                        {loading
                            ? convertLetters("Generating...")
                            : convertLetters("Generate Linking Code")}
                    </span>
                </button>
            ) : (
                <div className="space-y-4">
                    <div className="rounded-lg border-2 border-blue-500 bg-blue-950/30 p-4">
                        <p className="font-garamond text-sm text-blue-200">
                            Copy this code and paste it into the extension on
                            Twitch
                        </p>
                    </div>

                    <div className="flex items-center gap-2 rounded-lg bg-slate-900/50 p-3">
                        <code className="flex-1 font-mono text-sm break-all text-slate-200">
                            {token}
                        </code>
                        <button
                            onClick={copyToClipboard}
                            className="bg-brand/20 hover:bg-brand/30 shrink-0 rounded p-2 transition-all"
                            title="Copy to clipboard"
                        >
                            {copied ? (
                                <CheckCircle className="h-5 w-5 text-green-400" />
                            ) : (
                                <Copy className="text-brand h-5 w-5" />
                            )}
                        </button>
                    </div>

                    <button
                        onClick={() => generateToken()}
                        disabled={loading}
                        className="bg-brand/50 hover:bg-brand/70 flex w-full items-center justify-center gap-2 rounded-lg px-6 py-2 font-bold text-white shadow-lg transition-all disabled:opacity-50"
                    >
                        <span className="font-garamond text-sm">
                            Generate New Code
                        </span>
                    </button>
                </div>
            )}

            {error && (
                <div className="mt-4 rounded-lg border-2 border-red-500 bg-red-950/30 p-4">
                    <p className="font-garamond text-sm text-red-200">
                        Error: {error.message}
                    </p>
                </div>
            )}
        </div>
    );
}
